import { i18nCrossUtil } from "@adgm/libs";
import { useMemo } from "react";

/**
 * 股东数据接口
 */
export interface IShareholderData {
  id?: string;
  name: string;
  shareholdingPercentage: string;
  nationalities: string[];
}

/**
 * 组件 Props 接口
 */
export interface IKycShareholderTableProps {
  value: IShareholderData[];
  onChange?: (shareholders: IShareholderData[]) => void;
}

/**
 * 列配置创建器类型
 */
export type TColumnConfigCreator<T = any> = (type: string, options?: any) => T;

/**
 * hook 返回值接口
 */
export interface IUseKycShareholderTableReturn<TColumnConfig = any> {
  /** 当前数据 */
  shareholders: IShareholderData[];
  /** 列配置 */
  shareholderColumns: TColumnConfig[];
  /** 数据变化处理函数 */
  handleChange: (data: any[]) => void;
  /** 国际化对象 */
  crossIntl: any;
}

/**
 * KYC 股东表格公共逻辑 Hook
 *
 * @param props - 组件props
 * @param createRenderer - 渲染器创建函数
 * @returns 表格所需的所有逻辑和数据
 */
export function useKycShareholderTable<TColumnConfig = any>(
  props: IKycShareholderTableProps,
  createRenderer: TColumnConfigCreator,
): IUseKycShareholderTableReturn<TColumnConfig> {
  const { value, onChange } = props;
  const crossIntl = i18nCrossUtil.t();
  // 定义股东表格的列配置
  const shareholderColumns = [
    {
      key: "name",
      title: crossIntl["shareholder.table.name"],
      dataIndex: "name",
      required: true,
      type: "text",
      render: createRenderer("text", { maxLength: 500 }),
    },
    {
      key: "shareholdingPercentage",
      title: crossIntl["shareholder.table.percentage"],
      dataIndex: "shareholdingPercentage",
      required: true,
      type: "percentage",
      render: createRenderer("percentage"),
    },
    {
      key: "nationalities",
      title: crossIntl["shareholder.table.nationality"],
      dataIndex: "nationalities",
      required: true,
      type: "nationality",
      render: createRenderer("nationality"),
    },
  ];

  const shareholdersValue = useMemo(() => {
    return value.map(item => {
      return {
        ...item,
      };
    });
  }, [value]);

  const handleChange = (data: any[]) => {
    // 转换回股东数据格式，确保新数据有ID
    const shareholders = data.map(item => {
      return {
        name: item.name || "",
        shareholdingPercentage: item.shareholdingPercentage || "",
        nationalities: item.nationalities || [],
      };
    });
    onChange?.(shareholders);
  };

  return {
    shareholders: shareholdersValue,
    shareholderColumns: shareholderColumns as TColumnConfig[],
    handleChange,
    crossIntl,
  };
}
